<template>
    <div>
        <div class="row">
            <div class="col-md-6 d-md-flex align-items-baseline">
                <h1 class="mr-2">List of Devices</h1>
            </div>
            <div class="col-md-6 pb-3">
                <input-search-icon
                    v-model="search"
                    @search="onSearch"
                    :placeholder="'Search Device'"
                    :haveItemOnRightSide="true"
                    :isDisabled="isLoading"
                >
                    <template v-slot:before>
                        <b-dropdown
                            variant="secondary"
                            class="pr-2"
                            :disabled="isLoading"
                        >
                            <template #button-content>
                                Status: {{ filterTypeSelected.label }}
                            </template>
                            <b-dropdown-item
                                v-for="option in deviceStatusOptions"
                                :key="option.label"
                                @click="onFilter(option)"
                            >
                                {{ option.label }}
                            </b-dropdown-item>
                        </b-dropdown>
                    </template>

                    <template v-slot:after>
                        <button
                            v-if="
                                isShowAddDeviceButton &&
                                _hasAccess('setting.device.add')
                            "
                            @click="onAddNewDevices"
                            type="button"
                            class="ml-2 btn btn-secondary"
                        >
                            <i class="fas fa-plus"></i>
                        </button>
                    </template>
                </input-search-icon>
            </div>
        </div>
        <div class="card card-attribute rounded-lg">
            <div class="card-body overflow-auto p-0">
                <div class="table-holder table-responsive">
                    <table class="table list">
                        <thead>
                            <tr>
                                <th class="width-20">
                                    <sorting
                                        :order-by="
                                            sort.column === 'manufacturer'
                                                ? sort.order
                                                : null
                                        "
                                        @sorting="
                                            orderBy =>
                                                (devicesList = onSorting(
                                                    orderBy,
                                                    'manufacturer'
                                                ))
                                        "
                                        text="Manufacturer:"
                                    ></sorting>
                                </th>
                                <th class="width-20">
                                    <sorting
                                        :order-by="
                                            sort.column === 'deviceName'
                                                ? sort.order
                                                : null
                                        "
                                        @sorting="
                                            orderBy =>
                                                (devicesList = onSorting(
                                                    orderBy,
                                                    'deviceName'
                                                ))
                                        "
                                        text="Device:"
                                    ></sorting>
                                </th>
                                <th class="width-20">
                                    <sorting
                                        :order-by="
                                            sort.column === 'displayName'
                                                ? sort.order
                                                : null
                                        "
                                        @sorting="
                                            orderBy =>
                                                (devicesList = onSorting(
                                                    orderBy,
                                                    'displayName'
                                                ))
                                        "
                                        text="Display Name:"
                                    ></sorting>
                                </th>
                                <th class="width-20">
                                    <sorting
                                        :order-by="
                                            sort.column === 'status'
                                                ? sort.order
                                                : null
                                        "
                                        @sorting="
                                            orderBy =>
                                                (devicesList = onSorting(
                                                    orderBy,
                                                    'status'
                                                ))
                                        "
                                        text="Status:"
                                    ></sorting>
                                </th>
                                <th class="width-15">Pending Tests:</th>
                                <th class="width-5"></th>
                            </tr>
                        </thead>
                        <tbody>
                            <template v-if="isLoading">
                                <tr>
                                    <td colspan="100">
                                        <LoaderList class="w-100" />
                                    </td>
                                </tr>
                            </template>
                            <template v-else>
                                <template v-if="devicesList.length > 0">
                                    <tr
                                        v-for="device in devicesList"
                                        :key="device.id"
                                    >
                                        <td>
                                            {{ device.manufacturer }}
                                        </td>

                                        <td>
                                            {{ device.deviceName }}
                                        </td>

                                        <td>
                                            {{ device.displayName }}
                                        </td>
                                        <td>
                                            <i
                                                v-if="device.status !== '-'"
                                                :class="device.statusIcon"
                                            ></i>
                                            {{ device.status }}
                                        </td>
                                        <td>{{ device.pendingStatus }}</td>
                                        <td
                                            class="text-right small text-nowrap"
                                        >
                                            <spinner
                                                v-if="device.isLoading"
                                            ></spinner>
                                            <div v-else>
                                                <span
                                                    class="cursor-pointer"
                                                    @click="onEdit(device)"
                                                    v-if="
                                                        _hasAccess(
                                                            'setting.device.edit'
                                                        )
                                                    "
                                                >
                                                    Edit
                                                </span>
                                                <span
                                                    v-if="canDelete(device)"
                                                    class="cursor-pointer"
                                                    @click="onDelete(device)"
                                                >
                                                    <span class="mx-1">
                                                        |
                                                    </span>
                                                    Delete
                                                </span>
                                            </div>
                                        </td>
                                    </tr>
                                </template>
                                <tr v-else>
                                    <td colspan="6">
                                        <i>No records available</i>
                                    </td>
                                </tr>
                            </template>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
        <div>
            <modal-edit-device
                ref="modalEditDevice"
                :device="selectedDevice"
                @device-updated="onDeviceUpdated"
            />
            <modal-add-device
                ref="modalAddDevice"
                @device-added="onDeviceAdded"
            />
            <modal-delete-device
                ref="modalDeleteDevice"
                @device-deleted="onDeviceDeleted"
            />
        </div>
    </div>
</template>
<script>
import DevicesListService from "@/services/setting/devices-list";
import { filter, isEmpty, isNil, trim, cloneDeep } from "lodash";
import EXTENSIONS from "@/constants/extensions";
import AccountService from "@/services/account";
import ModalEditDevice from "@/components/setting-devices/modal-edit-device.vue";
import ModalAddDevice from "@/components/setting-devices/modal-add-device.vue";
import ModalDeleteDevice from "@/components/setting-devices/modal-delete-device.vue";

const DEVICE_ONLINE = {
    value: 1,
    label: "Online"
};
const DEVICE_OFFLINE = {
    value: 0,
    label: "Offline"
};
const DEVICE_All_STATUS = {
    value: 2,
    label: "All"
};
const DEVICE_N_A = {
    value: "-",
    label: "N/A"
};

export default {
    components: {
        LoaderList: () => import("@/components/elements/loader-list.vue"),
        Sorting: () => import("@/module/commons/sorting.vue"),
        inputSearchIcon: () => import("@/module/commons/input-search-icon.vue"),
        Spinner: () => import("@/components/elements/spinner.vue"),
        ModalEditDevice,
        ModalAddDevice,
        ModalDeleteDevice
    },
    data() {
        return {
            isLoading: false,
            search: "",
            filterTypeSelected: DEVICE_All_STATUS,
            filterTypeOptions: [],
            sort: {
                column: "manufacturer",
                order: "asc"
            },
            devicesList: [], // this holds the currently displayed list
            originalDevicesList: [], // this holds the full unfiltered list retrieved from API
            deviceStatusOptions: [
                DEVICE_All_STATUS,
                DEVICE_ONLINE,
                DEVICE_OFFLINE
            ],
            selectedDevice: {},
            eligibility: {
                showAddDevice: false,
                stripe: false,
                gravity: false
            }
        };
    },
    computed: {
        isShowAddDeviceButton() {
            return this.eligibility.showAddDevice;
        }
    },
    async mounted() {
        this.sort.column = "manufacturer";
        this.sort.order = "asc";
        await this.getDevicesList();
        await this.checkAddDeviceEligibility();
    },
    watch: {},
    methods: {
        async checkAddDeviceEligibility() {
            try {
                const setting = await this._getSetting();
                const businessId = setting.business.id;

                // check payment tab enabled/disabled
                const onboardStatus = await AccountService.checkOnboardAccount(
                    businessId
                );

                // Determine if Payment Tab is NOT enabled
                const paymentTabEnabled =
                    onboardStatus?.code === 200 &&
                    onboardStatus?.account == true;

                const paymentTabNotEnabled = !paymentTabEnabled;

                // Check if Stripe or Gravity extensions are enabled
                const hasStripe = this._isExtensionEnabled("STRIPE");
                const hasGravity = this._isExtensionEnabled("GRAVITY");

                this.eligibility = {
                    showAddDevice:
                        paymentTabNotEnabled && (hasStripe || hasGravity),
                    stripe: hasStripe,
                    gravity: hasGravity
                };
            } catch (error) {
                this.eligibility = {
                    showAddDevice: false,
                    stripe: false,
                    gravity: false
                };
            }
        },
        async getDevicesList() {
            this.isLoading = true;
            this.deviceStatusOptions = [
                DEVICE_All_STATUS,
                DEVICE_ONLINE,
                DEVICE_OFFLINE
            ];

            try {
                const res = await DevicesListService.getDeviceLists();

                if (res && res.status == 200) {
                    const resources = res.data.response ?? {};
                    let devices = [];
                    let hasEmptyStatus = false;

                    const checkIfHasEmptyStatus = status => {
                        return (
                            isNil(status) ||
                            trim(status) === "" ||
                            status === "-"
                        );
                    };
                    const analyzersList = (resources.analyzers || []).map(
                        analyzer => {
                            if (
                                checkIfHasEmptyStatus(analyzer.analyzer_status)
                            ) {
                                hasEmptyStatus = true;
                            }
                            return {
                                id: analyzer.id,
                                manufacturer: "Abaxis",
                                isAnalyzer: true,
                                deviceName: analyzer.api_name,
                                displayName: analyzer.clinic_display_name,
                                clinic_display_name:
                                    analyzer.clinic_display_name,
                                status: analyzer.analyzer_status,
                                statusId: this.getStatusId(
                                    analyzer.analyzer_status
                                ),
                                statusIcon: this.getStatusIcon(
                                    analyzer.analyzer_status
                                ),
                                pendingStatus:
                                    analyzer.pending_tests !== null
                                        ? analyzer.pending_tests
                                        : "-",
                                isLoading: false
                            };
                        }
                    );

                    const devicesList = (resources.devices || []).map(
                        device => {
                            if (checkIfHasEmptyStatus(device.device_status)) {
                                hasEmptyStatus = true;
                            }
                            return {
                                id: device.id,
                                manufacturer: device.manufacturer,
                                isAnalyzer: false,
                                deviceName: device.name,
                                displayName: device.display_name || device.name,
                                status: device.device_status,
                                source: device.source,
                                statusId: this.getStatusId(
                                    device.device_status
                                ),
                                statusIcon: this.getStatusIcon(
                                    device.device_status
                                ),
                                pendingStatus: "-",
                                isLoading: false
                            };
                        }
                    );

                    // combine analyzers and devices
                    devices = [...analyzersList, ...devicesList];

                    // save into originalDevicesList
                    this.originalDevicesList = devices;

                    // apply search + sorting immediately
                    this.searchFilterDevicesList();

                    // 'N/A' options
                    if (hasEmptyStatus) {
                        this.deviceStatusOptions.push(DEVICE_N_A);
                    }
                } else {
                    throw new Error("Error: Unable to get the devices list.");
                }
            } catch (error) {
                console.error(error);
                this._message(
                    "Error: Unable to get the devices list.",
                    "danger"
                );
            } finally {
                this.isLoading = false;
            }
        },
        getStatusId(status) {
            const statusLowercase = status.toLowerCase();
            if (statusLowercase == "online" || statusLowercase == "busy") {
                return DEVICE_ONLINE.value;
            } else if (statusLowercase == "offline") {
                return DEVICE_OFFLINE.value;
            } else {
                return DEVICE_N_A.value;
            }
        },
        getStatusIcon(status) {
            return status.toLowerCase() === "online"
                ? "fas fa-circle icon-status online"
                : "fas fa-circle icon-status offline";
        },
        onSorting(orderBy, col, list) {
            list = list || this.devicesList;
            this.sort.order = orderBy;
            this.sort.column = col;

            return [...list].sort((a, b) => {
                const aVal = a[col]?.toString().toLowerCase() ?? "";
                const bVal = b[col]?.toString().toLowerCase() ?? "";

                return orderBy === "asc"
                    ? aVal.localeCompare(bVal)
                    : bVal.localeCompare(aVal);
            });
        },
        onSearch(text) {
            this.search = text;
            this.searchFilterDevicesList();
        },
        onEdit(device) {
            // device.isLoading = true;
            this.selectedDevice = { ...device };
            this.$refs.modalEditDevice.open();
        },
        async onDeviceUpdated(updatedDevice) {
            this.isLoading = true;
            try {
                // create a completely new array to force reactivity
                const newOriginalList = this.originalDevicesList.map(device => {
                    if (device.id === updatedDevice.id) {
                        // return a new object with all required fields
                        return {
                            ...device,
                            displayName:
                                updatedDevice.displayName || device.deviceName,
                            status: updatedDevice.status || device.status
                        };
                    }
                    return device;
                });

                // reactively update the original list
                this.originalDevicesList = newOriginalList;

                // update the displayed list
                this.searchFilterDevicesList();
            } catch (error) {
                this._message(error.message, "danger");
            } finally {
                this.isLoading = false;
            }
        },
        onAddNewDevices() {
            if (this.eligibility.stripe) {
                this.$refs.modalAddDevice.open(EXTENSIONS.CODE.CODE_STRIPE);
            } else if (this.eligibility.gravity) {
                this.$refs.modalAddDevice.open(EXTENSIONS.CODE.CODE_GRAVITY);
            }
        },
        async onDeviceAdded() {
            this.isLoading = true;
            await this.getDevicesList();
            this.isLoading = false;
        },
        onFilter(option) {
            this.filterTypeSelected = option;
            this.searchFilterDevicesList();
        },
        searchFilterDevicesList() {
            // create a new filtered list
            let filteredList = this.originalDevicesList.filter(device => {
                const matchesStatus =
                    this.filterTypeSelected.value === DEVICE_All_STATUS.value ||
                    device.statusId === this.filterTypeSelected.value;

                const matchesSearch =
                    !this.search ||
                    device.manufacturer
                        .toLowerCase()
                        .includes(this.search.toLowerCase()) ||
                    device.deviceName
                        .toLowerCase()
                        .includes(this.search.toLowerCase()) ||
                    device.displayName
                        .toLowerCase()
                        .includes(this.search.toLowerCase());

                return matchesStatus && matchesSearch;
            });

            // apply sorting
            this.devicesList = this.onSorting(
                this.sort.order,
                this.sort.column,
                filteredList
            );
        },
        canDelete(device) {
            return (
                device.source === EXTENSIONS.CODE.CODE_GRAVITY &&
                this._hasAccess("setting.device.edit")
            );
        },
        onDelete(device) {
            this.$refs.modalDeleteDevice.open(device);
        },
        async onDeviceDeleted(deviceId) {
            this.originalDevicesList = this.originalDevicesList.filter(
                d => d.id !== deviceId
            );
            this.searchFilterDevicesList();
        }
    }
};
</script>
<style scoped>
.card-attribute {
    padding: 1rem;
}

.width-20 {
    width: 20%;
}

.width-15 {
    width: 15%;
}

.width-5 {
    width: 5%;
}
.mx-1 {
    margin-left: 0.1rem !important;
    margin-right: 0.1rem !important;
}
</style>
